import React from "react";

import twMerge from "@v1/components/common/designSystem/utils/twMerge";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import {
  CircleAlert,
  CircleCheck,
  Info,
  MessageCircle,
  Rocket,
  TriangleAlert,
  X,
} from "lucide-react";

type InlineAlertProps = React.PropsWithChildren<{
  variant:
    | "success"
    | "info"
    | "error"
    | "warning"
    | "neutral"
    | "announcement";
  UNSAFE_className?: string;
  layoutClassName?: string;
  multiline?: boolean;
  onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
  centerAlign?: boolean;
}>;

const InlineAlert: React.FC<InlineAlertProps> = ({
  variant,
  children,
  UNSAFE_className,
  layoutClassName,
  multiline = false,
  onClose,
  endEnhancer,
  startEnhancer,
  centerAlign = false,
}) => {
  return (
    <div
      className={twMerge(
        "text-default border-0.5 flex items-center gap-2 rounded-[6px] p-2 text-xs",
        multiline && "items-start p-3",
        centerAlign && "justify-center",
        variantClassNameMapping[variant],
        layoutClassName,
        UNSAFE_className,
      )}
    >
      {startEnhancer ?? startEnhancerMapping[variant]}
      <div
        className={twMerge(
          "flex grow justify-between items-center gap-3",
          centerAlign && "grow-0",
          multiline && "flex-col flex-wrap items-start gap-2",
        )}
      >
        {children}
        {endEnhancer}
      </div>
      {onClose && (
        <DeprecatedIconButton
          size="sm"
          variant="tertiary"
          onClick={onClose}
          icon={<X size={16} />}
          UNSAFE_className="hover:bg-transparent hover:opacity-75"
        />
      )}
    </div>
  );
};

const variantClassNameMapping = {
  success: "bg-emerald-soft border-emerald-border",
  info: "bg-sky-soft border-sky/20",
  error: "bg-rose-soft border-rose-border",
  warning: "bg-amber-soft border-amber-border",
  neutral: "bg-neutral-soft border-neutral-border",
  announcement: "bg-fuchsia-soft border-fuchsia-border",
};

const startEnhancerMapping = {
  success: <CircleCheck size={16} className="text-emerald shrink-0" />,
  info: <Info size={16} className="text-sky shrink-0" />,
  error: <CircleAlert size={16} className="text-rose shrink-0" />,
  warning: <TriangleAlert size={16} className="text-amber shrink-0" />,
  neutral: <MessageCircle size={16} className="text-neutral shrink-0" />,
  announcement: <Rocket size={16} className="text-fuchsia shrink-0" />,
};

export default InlineAlert;
